'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ShoppingBag } from 'lucide-react';
import { Link } from '@/lib/i18n/navigation';
import { useCart } from '@/lib/cart/store';

interface CartIconProps {
  onClick?: () => void;
  /** Si es true, es link directo a /carrito; si false, button que ejecuta onClick. */
  asLink?: boolean;
  showLabel?: boolean;
}

/**
 * Cart icon con badge de count, sincronizado con el Zustand store.
 * En SSR muestra 0 hasta que hidrata (evita mismatch hydration).
 */
export function CartIcon({ onClick, asLink = false, showLabel = false }: CartIconProps) {
  const t = useTranslations('nav');
  const tCart = useTranslations('cart');
  const totalUnits = useCart((s) => s.totalUnits());
  const hydrated = useCart((s) => s.hydrated);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const count = mounted && hydrated ? totalUnits : 0;

  const content = (
    <>
      <div className="relative">
        <ShoppingBag className="h-5 w-5" strokeWidth={1.25} />
        {count > 0 ? (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-accent)] px-1 text-[10px] font-bold text-[var(--color-accent-fg)]">
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </div>
      {showLabel ? (
        <span className="hidden lg:inline">
          {t('cart')} ({count})
        </span>
      ) : null}
    </>
  );

  const className =
    'type-nav flex items-center gap-2 rounded-sm px-2 py-2 text-[var(--color-fg-muted)] transition-colors hover:bg-[var(--color-surface)] hover:text-[var(--color-accent)]';

  if (asLink) {
    return (
      <Link href="/carrito" aria-label={t('ariaCart')} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      aria-label={tCart('miniCartTitle')}
      onClick={onClick}
      className={className}
    >
      {content}
    </button>
  );
}
